import React, { useEffect, useMemo, useState } from "react";
import { Form, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AiOutlineReload } from "react-icons/ai";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { BiEdit } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { BsEyeFill } from "react-icons/bs";
import axios from "axios";
import swal from "sweetalert";
import { fetchpointTableList } from "../../../redux/getDropDownReducer/getPointTableList";

const { Option } = Select;

const CompetitionV2Races = () => {
  const { state } = useLocation();
  const presetCompetitionId = state?.competitionId || "";
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState("");
  const [v2Competitions, setV2Competitions] = useState([]);
  const [selectedCompetitionDetail, setSelectedCompetitionDetail] = useState(null);
  const [competitionSearch, setCompetitionSearch] = useState("");
  const [competitionRaceOptions, setCompetitionRaceOptions] = useState([]);
  const [raceSearch, setRaceSearch] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editRaceOptions, setEditRaceOptions] = useState([]);
  const [editRaceSearch, setEditRaceSearch] = useState("");
  const [editForm] = Form.useForm();
  const [showRaceLeaderboardModal, setShowRaceLeaderboardModal] = useState(false);
  const [raceLeaderboardRows, setRaceLeaderboardRows] = useState([]);
  const [raceLeaderboardLoading, setRaceLeaderboardLoading] = useState(false);
  const [raceLeaderboardTitle, setRaceLeaderboardTitle] = useState("");

  const { data: pointTableList } = useSelector((state) => state.pointTableList);

  const selectedCompetition = useMemo(
    () =>
      selectedCompetitionDetail ||
      v2Competitions.find((item) => item._id === selectedCompetitionId) ||
      null,
    [selectedCompetitionDetail, selectedCompetitionId, v2Competitions]
  );

  const competitionOptions = useMemo(
    () => {
      const base = v2Competitions.map((item) => ({
        value: item._id,
        label: `${item.NameEn || "N/A"} (${item.CompetitionCategory}) [${item.Status}]`,
      }));
      if (
        selectedCompetitionDetail?._id &&
        !base.some((item) => item.value === selectedCompetitionDetail._id)
      ) {
        base.unshift({
          value: selectedCompetitionDetail._id,
          label: `${selectedCompetitionDetail.NameEn || "N/A"} (${selectedCompetitionDetail.CompetitionCategory}) [${selectedCompetitionDetail.Status}]`,
        });
      }
      return base;
    },
    [v2Competitions, selectedCompetitionDetail]
  );

  const raceOptions = useMemo(() => competitionRaceOptions, [competitionRaceOptions]);

  const pointTableOptions = useMemo(
    () => {
      const expectedType = selectedCompetition?.CompetitionCategory === "pick" ? "Pick" : "Cast";
      const filtered = (pointTableList || [])
        .filter((item) => item?.Type === expectedType)
        .map((item) => ({
          value: item._id,
          label:
            item?.Group_NameDataOfCompetition?.NameEn ||
            item?.Group_NameDataOfCompetition?.NameAr ||
            item?.shortCode ||
            item?._id,
        }));

      // In edit mode, keep currently-linked point table visible by name
      // even if its type no longer matches the current category filter.
      if (editingRow?.PointTableOfRace) {
        const exists = filtered.some((opt) => opt.value === editingRow.PointTableOfRace);
        if (!exists) {
          const current = (pointTableList || []).find(
            (item) => item?._id === editingRow.PointTableOfRace
          );
          if (current) {
            filtered.unshift({
              value: current._id,
              label:
                current?.Group_NameDataOfCompetition?.NameEn ||
                current?.Group_NameDataOfCompetition?.NameAr ||
                current?.shortCode ||
                current?._id,
            });
          }
        }
      }
      return filtered;
    },
    [pointTableList, selectedCompetition?.CompetitionCategory, editingRow?.PointTableOfRace]
  );

  const fetchCompetitions = async (keyword = "") => {
    const params = new URLSearchParams();
    params.set("includeRaces", "false");
    params.set("page", "1");
    params.set("size", "10");
    if (keyword.trim()) {
      params.set("keyword", keyword.trim());
    }
    const { data } = await axios.get(`${window.env.API_URL}/competition-v2/admin?${params.toString()}`);
    setV2Competitions(data?.data || []);
  };

  const fetchSelectedCompetitionDetail = async (competitionId) => {
    if (!competitionId) {
      setSelectedCompetitionDetail(null);
      return;
    }
    const { data } = await axios.get(`${window.env.API_URL}/competition-v2/${competitionId}/admin`);
    setSelectedCompetitionDetail(data?.data || null);
  };

  const fetchRaceOptionsForCompetition = async (
    competitionId,
    keyword = "",
    includeRaceId = ""
  ) => {
    if (!competitionId) {
      setCompetitionRaceOptions([]);
      return;
    }
    const params = new URLSearchParams();
    if (keyword) params.set("keyword", keyword);
    if (includeRaceId) params.set("includeRaceId", includeRaceId);
    params.set("page", "1");
    params.set("size", "50");
    const { data } = await axios.get(
      `${window.env.API_URL}/competition-v2/${competitionId}/race-options?${params.toString()}`
    );
    const mapped = (data?.data || []).map((item) => {
      const raceName = item?.RaceNameModelData?.NameEn || item._id;
      const startTime = item?.StartTime
        ? new Date(item.StartTime).toLocaleString()
        : "N/A";
      return {
        value: item._id,
        label: `${raceName} - ${startTime}`,
      };
    });
    setCompetitionRaceOptions(mapped);
    return mapped;
  };

  const loadDropdowns = () => {
    dispatch(fetchpointTableList());
  };

  useEffect(() => {
    loadDropdowns();
    fetchCompetitions("").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (presetCompetitionId) {
      setSelectedCompetitionId(presetCompetitionId);
    }
  }, [presetCompetitionId]);

  useEffect(() => {
    fetchRaceOptionsForCompetition(selectedCompetitionId, "").catch(() => {
      setCompetitionRaceOptions([]);
    });
  }, [selectedCompetitionId]);

  useEffect(() => {
    fetchSelectedCompetitionDetail(selectedCompetitionId).catch(() => {
      setSelectedCompetitionDetail(null);
    });
  }, [selectedCompetitionId]);

  const onAddRace = async (values) => {
    if (!selectedCompetitionId) {
      swal("Error!", "Please select a Competition", "error");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/races`,
        {
          Race: values.Race,
          PointTableOfRace: values.PointTableOfRace,
        }
      );
      await fetchSelectedCompetitionDetail(selectedCompetitionId);
      await fetchRaceOptionsForCompetition(selectedCompetitionId, raceSearch);
      form.resetFields(["Race", "PointTableOfRace"]);
      swal("Success!", "Race added to competition", "success");
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (row) => {
    setEditingRow(row);
    setShowEditModal(true);
    const options = await fetchRaceOptionsForCompetition(
      selectedCompetitionId,
      "",
      row.Race
    );
    setEditRaceOptions(options || []);
    editForm.setFieldsValue({
      Race: row.Race,
      PointTableOfRace: row.PointTableOfRace,
    });
  };

  const onUpdateRaceLink = async (values) => {
    if (!editingRow?._id) return;
    setLoading(true);
    try {
      await axios.put(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/races/${editingRow._id}`,
        {
          Race: values.Race,
          PointTableOfRace: values.PointTableOfRace,
        }
      );
      await fetchSelectedCompetitionDetail(selectedCompetitionId);
      await fetchRaceOptionsForCompetition(selectedCompetitionId, raceSearch);
      setShowEditModal(false);
      setEditingRow(null);
      swal("Success!", "Competition race updated", "success");
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const onDeleteRaceLink = async (row) => {
    if (!row?._id) return;
    swal({
      title: "Are you sure?",
      text: "Do you want to remove this race from competition?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      setLoading(true);
      try {
        await axios.delete(
          `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/races/${row._id}`
        );
        await fetchSelectedCompetitionDetail(selectedCompetitionId);
        await fetchRaceOptionsForCompetition(selectedCompetitionId, raceSearch);
        swal("Success!", "Race removed from competition", "success");
      } catch (error) {
        const message = error?.response?.data?.message || error.message || "Request failed";
        swal("Error!", message, "error");
      } finally {
        setLoading(false);
      }
    });
  };

  const openRaceLeaderboardModal = async (row) => {
    if (!selectedCompetitionId) {
      swal("Error!", "Please select a Competition", "error");
      return;
    }
    setRaceLeaderboardTitle(
      row?.RaceDataOfCompetitionV2?.RaceNameModelData?.NameEn || row?.Race || "Race"
    );
    setShowRaceLeaderboardModal(true);
    setRaceLeaderboardLoading(true);
    try {
      const { data } = await axios.get(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/races/${row.Race}/leaderboard`
      );
      setRaceLeaderboardRows(data?.data || []);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
      setShowRaceLeaderboardModal(false);
    } finally {
      setRaceLeaderboardLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="rightsidedata">
        <div style={{ marginTop: "30px" }}>
          <div className="Header ">
            <h4>Competition Races</h4>
            <OverlayTrigger overlay={<Tooltip id="tooltip-refresh-v2-races">Fetch New</Tooltip>}>
              <span
                className="addmore"
                onClick={() => {
                  loadDropdowns();
                  fetchCompetitions(competitionSearch).catch(() => {});
                  fetchSelectedCompetitionDetail(selectedCompetitionId).catch(() => {
                    setSelectedCompetitionDetail(null);
                  });
                  fetchRaceOptionsForCompetition(selectedCompetitionId, raceSearch).catch(() => {});
                }}
              >
                <AiOutlineReload />
              </span>
            </OverlayTrigger>
          </div>

          <div className="form" style={{ marginTop: "20px" }}>
            <div className="row mainrow">
              <div className="col-sm">
                <Form.Item label="Competition" style={{ marginBottom: 0 }}>
                  <Select
                    value={selectedCompetitionId || undefined}
                    onChange={setSelectedCompetitionId}
                    placeholder="Select Competition"
                    showSearch
                    filterOption={false}
                    onSearch={(value) => {
                      setCompetitionSearch(value);
                      fetchCompetitions(value).catch(() => {});
                    }}
                    onFocus={() => {
                      fetchCompetitions(competitionSearch).catch(() => {});
                    }}
                  >
                    {competitionOptions.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="form" style={{ marginTop: "20px" }}>
            <Form form={form} onFinish={onAddRace} name="competition_v2_add_race_only">
              <div className="row mainrow">
                <div className="col-sm">
                  <Form.Item
                    name="Race"
                    label="Race"
                    rules={[{ required: true, message: "Race is required" }]}
                  >
                    <Select
                      showSearch
                      filterOption={false}
                      placeholder="Select race"
                      onSearch={(value) => {
                        setRaceSearch(value);
                        fetchRaceOptionsForCompetition(selectedCompetitionId, value).catch(() => {});
                      }}
                      onFocus={() =>
                        fetchRaceOptionsForCompetition(selectedCompetitionId, raceSearch).catch(() => {})
                      }
                    >
                      {raceOptions.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="col-sm">
                  <Form.Item
                    name="PointTableOfRace"
                    label="Point Table"
                    rules={[{ required: true, message: "Point table is required" }]}
                  >
                    <Select showSearch placeholder="Select point table">
                      {pointTableOptions.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
              <div className="ButtonSection" style={{ justifyContent: "end" }}>
                <button className="SubmitButton" type="submit" disabled={loading || !selectedCompetitionId}>
                  Add Race
                </button>
              </div>
            </Form>
          </div>

          <div className="form" style={{ marginTop: "20px" }}>
            <div style={{ marginBottom: "10px", marginLeft: "20px" }}>
              <h5>Linked Races In Competition</h5>
            </div>
            <div className="div_maintb" style={{ maxHeight: "320px", overflowY: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>#</th>
                    <th>Race</th>
                    <th>Race Number</th>
                    <th>Start Time</th>
                    <th>Point Table</th>
                    <th>Point Type</th>
                    <th>Settled</th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedCompetitionId ? (
                    <tr>
                      <td colSpan={8}>Select a competition to view linked races.</td>
                    </tr>
                  ) : !selectedCompetition?.CompetitionDataOfRaceV2?.length ? (
                    <tr>
                      <td colSpan={8}>No races linked yet.</td>
                    </tr>
                  ) : (
                    selectedCompetition.CompetitionDataOfRaceV2.map((row, idx) => (
                      <tr key={row._id}>
                        <td className="table_delete_btn1">
                          <BsEyeFill onClick={() => openRaceLeaderboardModal(row)} />
                          <BiEdit onClick={() => openEditModal(row)} />
                          <MdDelete
                            style={{ marginLeft: "8px", fontSize: "18px" }}
                            onClick={() => onDeleteRaceLink(row)}
                          />
                        </td>
                        <td>{idx + 1}</td>
                        <td>{row?.RaceDataOfCompetitionV2?.RaceNameModelData?.NameEn || row.Race}</td>
                        <td>{row?.RaceDataOfCompetitionV2?.RaceNumber || "N/A"}</td>
                        <td>{row?.RaceDataOfCompetitionV2?.StartTime || "N/A"}</td>
                        <td>
                          {row?.PointTableOfRaceofRaceV2?.Group_NameDataOfCompetition?.NameEn ||
                            row?.PointTableOfRaceofRaceV2?.shortCode ||
                            row.PointTableOfRace}
                        </td>
                        <td>{row?.PointTableOfRaceofRaceV2?.Type || "N/A"}</td>
                        <td>{row.SettledAtUtc ? "Yes" : "No"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingRow(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Competition Race</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form form={editForm} onFinish={onUpdateRaceLink} name="competition_v2_edit_race">
            <div className="row mainrow">
              <div className="col-sm">
                <Form.Item
                  name="Race"
                  label="Race"
                  rules={[{ required: true, message: "Race is required" }]}
                >
                  <Select
                    showSearch
                    filterOption={false}
                    placeholder="Select race"
                    onSearch={(value) => {
                      setEditRaceSearch(value);
                      fetchRaceOptionsForCompetition(
                        selectedCompetitionId,
                        value,
                        editingRow?.Race || ""
                      )
                        .then((opts) => setEditRaceOptions(opts || []))
                        .catch(() => {});
                    }}
                    onFocus={() =>
                      fetchRaceOptionsForCompetition(
                        selectedCompetitionId,
                        editRaceSearch,
                        editingRow?.Race || ""
                      )
                        .then((opts) => setEditRaceOptions(opts || []))
                        .catch(() => {})
                    }
                  >
                    {editRaceOptions.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="col-sm">
                <Form.Item
                  name="PointTableOfRace"
                  label="Point Table"
                  rules={[{ required: true, message: "Point table is required" }]}
                >
                  <Select showSearch placeholder="Select point table">
                    {pointTableOptions.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>
            <div className="ButtonSection" style={{ justifyContent: "end" }}>
              <button className="SubmitButton" type="submit" disabled={loading}>
                Save Changes
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal
        show={showRaceLeaderboardModal}
        onHide={() => setShowRaceLeaderboardModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{raceLeaderboardTitle} - Race Leaderboard</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="div_maintb" style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "95%", minWidth: "0", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>Rank</th>
                  <th style={{ width: "220px" }}>Subscriber</th>
                  <th style={{ width: "200px" }}>Horses</th>
                  <th style={{ width: "50px" }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {raceLeaderboardLoading ? (
                  <tr>
                    <td colSpan={4}>Loading race leaderboard...</td>
                  </tr>
                ) : raceLeaderboardRows.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No race leaderboard entries found.</td>
                  </tr>
                ) : (
                  raceLeaderboardRows.map((row, index) => (
                    <tr key={row._id || `${row.SubscriberID}-${index}`}>
                      <td>{index + 1}</td>
                      <td>
                        {row?.CompetitionSubscriberIDDataV2?.FirstName || ""}{" "}
                        {row?.CompetitionSubscriberIDDataV2?.LastName || ""}
                      </td>
                      <td>
                        {(row?.Horses || [])
                          .slice()
                          .sort((a, b) => Number(a?.Slot || 0) - Number(b?.Slot || 0))
                          .map((h) => (
                            <div key={`${h?.HorseID || "horse"}-${h?.Slot || 0}`}>
                              #{h?.Slot || "-"} - {h?.NameEn || h?.NameAr || h?.HorseID}
                            </div>
                          ))}
                      </td>
                      <td>{row?.TotalPoint ?? 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CompetitionV2Races;
